import { ChangeDetectionStrategy, Component, computed, effect, inject, Input, OnInit, signal, Signal, untracked, HostListener } from '@angular/core';
import { PlayerService } from '../../data-access/player.service';
import { PlayerItemSource } from '../../data-access/player.config';
import { RoundedIconButton } from 'app/shared/components/rounded-icon-button/rounded-icon-button';
import { SkinningService } from '@core/skin';
import { clpp } from '@castlabs/prestoplay';
import { CommonModule, DatePipe } from '@angular/common';
import { SliderModule } from 'primeng/slider';
import { FormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { DetailsService } from '@features/details/data-access/details.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { TranscodeImageDirective } from '@shared/directives/transcode-image.directive';
import { LiveTimelineService } from '../../data-access/live-timeline.service';

@Component({
  selector: 'player-compact-controls',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoPipe,
    RoundedIconButton,
    DatePipe,
    FormsModule,
    SliderModule,
    TranscodeImageDirective
  ],
  templateUrl: './player-compact-controls.component.html',
  styleUrl: './player-compact-controls.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlayerCompactControlsComponent {
  public skinning = inject(SkinningService);
  private playerService = inject(PlayerService);
  private detailsService = inject(DetailsService);
  private breakpointObserver = inject(BreakpointObserver);
  private liveTimeline = inject(LiveTimelineService);
  public readonly progressPercentage: Signal<number> = this.playerService.getProgressPercentage();
  public readonly progress: Signal<number> = this.playerService.getProgress();
  public readonly item: Signal<any> = this.playerService.getItem();
  public isMuted = this.playerService.isPlayerMuted();
  public isFullscreen = this.playerService.isFullscreen();
  private state = this.playerService.getState();
  public isTabletPortrait = toSignal(this.breakpointObserver.observe([Breakpoints.TabletPortrait]));
  public isCurrentItemLive = this.playerService.isCurrentItemLive;
  public isCasting = this.playerService.isCasting();
  public itemTitle = computed(() => {
    return this.item()?.title || this.item()?.name;
  });
  public itemLogo = computed(() => {
    // `icon` is the fallback for live channels — the channel-play payload has neither
    // posterLandscape nor channelIcon, and an undefined src renders a broken image.
    return this.item()?.posterLandscape || this.item()?.channelIcon || this.item()?.icon;
  });
  // A channel-play payload carries no poster, so a live channel (radio above all) showed only
  // the black panel. Fall back to the poster of the programme airing on it.
  public itemPosterUrl = computed(() => {
    return this.item()?.posterLandscape || this.liveTimeline.currentEpg()?.imagePath;
  });
  public isPlaying = computed(() => {
    return this.state() == clpp.Player.State.PLAYING
  })
  public duration = computed(() => {
    if (this.progress() && this.playerService.getDuration() != Infinity) {
      return this.playerService.getDuration();
    }
    return 0;
  })
  public volume: Signal<number> = this.playerService.getVolume();
  private seriesEpisodeWatchData = this.playerService.getSeriesEpisodeWatchData();
  public readonly isSeries = computed<boolean>(() => {
    return Boolean(this.seriesEpisodeWatchData())
  })

  public isChannel = computed(() => {
    return [PlayerItemSource.TLIVE, PlayerItemSource.THEPG].includes(this.item()?.ng_source);
  })

  public isControlDisabled = computed(() => {
    return this.isCurrentItemLive() && (!this.liveTimeStart() || !this.liveTimeEnd())
  })

  // Live timeline — shared with the full controls. These used to be local copies that
  // read the play item only, which is empty for a channel played through
  // /channels/{id}/play: the floating player then showed no times, a dead scrubber and
  // (via isControlDisabled) dead transport buttons for the whole of a live cast.
  public isLive = this.liveTimeline.isLive;
  public liveTimeEnd = this.liveTimeline.liveTimeEnd;
  public liveTimeStart = this.liveTimeline.liveTimeStart;
  public liveSeekMin = this.liveTimeline.liveSeekMin;
  public liveProgress = this.liveTimeline.liveProgress;
  // Same live pause / scrub / start-over gating as the full controls — while casting,
  // these are what the receiver enforces too.
  public isSeekEnabled = this.liveTimeline.isSeekEnabled;
  public isRestartEnabled = this.liveTimeline.isRestartEnabled;
  public isPauseEnabled = this.liveTimeline.isPauseEnabled;

  public seekPosition = signal(0);
  public cursorPosition = signal(0);
  private lastState: clpp.Player.State | null = null;

  constructor() {
    effect(() => {
      const state = this.state()

      if (state == clpp.Player.State.BUFFERING && this.lastState == clpp.Player.State.PREPARING) {
        untracked(() => {
          const item = this.item();
          const seriesEpisodeWatchData = this.seriesEpisodeWatchData();
          const bookmark = seriesEpisodeWatchData ? seriesEpisodeWatchData?.watchData?.bookmarkEpisodes.find(episode => episode.episodeReferenceId == item.referenceId)?.bookmark : item.bookmark;
          if (bookmark) {
            this.playerService.setPosition(bookmark).subscribe();
          }
        })
      }

      this.lastState = state;
    })

  }

  onPlay() {
    // A live channel that can be paused but not scrubbed resumes at the live edge — so does a
    // pause that outlived its programme into one that forbids pausing.
    if (this.isCurrentItemLive() && (!this.isSeekEnabled() || !this.isPauseEnabled())) {
      this.onLive();
    }
    this.playerService.play().subscribe();
  }

  onPause() {
    // The keyboard (space / k) reaches here past the disabled button.
    if (!this.isPauseEnabled()) return;
    this.playerService.pause().subscribe();
  }

  onReplay() {
    if (!this.isSeekEnabled() || !this.isRestartEnabled()) return;
    // Live: position 0 doesn't exist in the sliding DVR window — the engine would clamp
    // the seek to seekRange.start, which advances in real time. Seek to the programme
    // start via the epoch-based live mapping instead.
    if (this.isCurrentItemLive()) {
      const start = this.liveTimeStart();
      if (start) {
        this.onSeekLive(Number(start));
        return;
      }
    }
    this.playerService.setPosition(0).subscribe();
  }

  onSeek(value: number) {
    this.playerService.setPosition(value).subscribe();
  }

  onLive() {
    this.onSeekLive(Date.now());
  }

  onSeekLive(value: number) {
    this.liveTimeline.seekToTimestamp(value).subscribe();
  }

  setSeek(value: number) {
    // The keyboard (arrows / j / l) reaches here past the disabled skip buttons.
    if (!this.isSeekEnabled()) return;
    this.playerService.setPosition(this.progress() + value).subscribe();
  }

  togglePip() {
    this.playerService.togglePip();
  }

  toggleCast() {
    this.playerService.toggleCast();
  }

  toggleFullscreen() {
    this.playerService.toggleFullscreen();
  }

  toggleMute() {
    this.playerService.toggleMute();
  }

  onVolume(percentage: number) {
    this.playerService.setVolume(percentage);
  }

  setSpeed(element: EventTarget | null) {
    const speed = (element as any).value;
    this.playerService.setSpeed(speed);
  }

  onSeekHover(event: MouseEvent) {
    const slider = event.currentTarget as HTMLElement;
    const rect = slider.getBoundingClientRect();

    // Calculate mouse position as a fraction (0-1)
    const percentage = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);

    // Convert fraction to seconds
    const positionInSeconds = Math.round(percentage * this.duration());

    this.seekPosition.set(positionInSeconds);
    this.cursorPosition.set(event.clientX);
  }

  onSeekClearHover() {
    this.seekPosition.set(null);
  }

  onClose() {
    this.playerService.close();
  }

  public openDetails() {
    const item = this.item();
    // The modal renders outside the player element — element-fullscreen would hide it entirely.
    // Drop fullscreen first (no-op outside it); playback continues under the modal.
    this.playerService.exitFullscreen();
    // A live item played through /channels/{id}/play is the CHANNEL (referenceId = channel id):
    // open its current event.
    this.detailsService.open({
      item: { ...item, contentType: item.ng_contentType || item.ng_source } as any,
      channelCurrentEvent: true,
    });
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }

    switch (event.key.toLowerCase()) {
      case ' ':
      case 'k':
        // preventDefault even on auto-repeat, or the held key scrolls the page — but toggle only
        // on the initial press: each toggle reports a play/pause event to the backend, and a held
        // key repeats keydown ~30x/s.
        event.preventDefault();
        if (!event.repeat) {
          this.isPlaying() ? this.onPause() : this.onPlay();
        }
        break;
      case 'arrowleft':
      case 'j':
        this.setSeek(-5);
        break;
      case 'arrowright':
      case 'l':
        this.setSeek(5);
        break;
      case 'arrowup':
        event.preventDefault();
        this.onVolume(Math.min(100, this.volume() + 5));
        break;
      case 'arrowdown':
        event.preventDefault();
        this.onVolume(Math.max(0, this.volume() - 5));
        break;
      case 'm':
        this.toggleMute();
        break;
      case 'f':
        this.toggleFullscreen();
        break;
    }
  }
}
